import React from 'react';
import cn from 'classnames';
import moment from 'moment';

import saveBooking from 'bl/booking/saveBooking.bl';
import PosDepositDialog from 'components/booking/PosDepositDialog.component';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import CurrencySign from 'components/price/CurrencySign.component';
import S from 'constants/string.const';
import { withTranslation } from 'providers/GILT.provider';
import { fromPropsToIsWeek, fromPropsToPathSegments } from 'utils/converters/props/path.util';
import updateIntercomElement from 'utils/dom/updateIntercomElement.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';

import BWContent from 'booking/components/common/BWContent';
import BWFooter from 'booking/components/common/BWFooter';

// Required for moment.duration().format() to work.
import 'moment-duration-format';

import CN from './BookingSummary.module.scss';

const CTAG = 'BookingSummary';
const L = logger(CTAG);

class BookingSummary extends React.Component {
  static defaultProps = {
    isEditBooking: false,
    updateAppointmentAndRepeatThunk: alwaysNoop, // TODO: seems unused, check and if so, remove it
  };

  constructor(props) {
    super(props);
    this.state = { dialogOpened: false, confirmDialogOpen: false, openPosModal: false, appointmentId: '' };
  }

  componentDidMount() {
    const { addedServices, salonId } = this.props;

    const segments = fromPropsToPathSegments(this.props);

    if ((segments[6] === 'edit' || segments[7] === 'edit') && addedServices.length === 0) {
      let appointmentId = parseInt(segments[7], 10);
      let nextPath = `/salon/${salonId}/day/${segments[4]}/appointment/edit/${appointmentId}/load`;
      if (fromPropsToIsWeek(this.props)) {
        appointmentId = parseInt(segments[8], 10);
        nextPath = `/salon/${salonId}/week/${segments[4]}/${segments[5]}/appointment/edit/${appointmentId}/load`;
      }

      navFor(CTAG, 'componentDidMount()', this.props).push({ pathname: nextPath });
    }

    updateIntercomElement({ show: false, tag: 'BookingSummary.componentDidMount()' });
  }

  componentWillUnmount() {
    updateIntercomElement({ show: true, tag: 'BookingSummary.componentWillUnmount()' });
  }

  onRequestClose = () => void this.setState({ dialogOpened: false, confirmDialogOpen: false });

  onExitAnyway = () => {
    const { cleanBookingData } = this.props;

    this.setState({ confirmDialogOpen: false });
    cleanBookingData();

    navFor(CTAG, 'onExitAnyway()', this.props).push({
      pathname: fromPropsToPathSegments(this.props)
        .filter((o) => o !== 'appointment' && o !== 'new' && o !== 'services')
        .join('/'),
    });
  };

  save = (oneOrAll = '') => {
    const cleanBookingData = (...args) => {
      const result = call('save() > cleanBookingData()', this.props.cleanBookingData, ...args);
      const event = { full: true, tag: 'BookingSummary.save()' };
      call('save() > feSidebarAppointmentClear()', this.props.feSidebarAppointmentClear, event);
      return result;
    };

    const result = saveBooking({
      ...this.props,
      oneOrAll,
      onStateChange: this.setState.bind(this),
      cleanBookingData,
    });
    L.debug('save()', 'saveBooking result:', result);
  };

  // noinspection JSVoidFunctionReturnValueUsed
  handleSave = this.save.bind(this);

  // FIXME: parent component depends on concrete child method
  // NOTE: breaks D from SOLID: Depend on abstractions, not on concretions
  saveBooking = this.save.bind(this); // eslint-disable-line react/sort-comp

  render() {
    const { isFetching, isFetchingService, t } = this.props;

    if (isFetchingService || isFetching) {
      return (
        <div data-bem="BookingSummary--fetching" data-is-fetching={String(isFetching)} data-is-fetching-service={String(isFetchingService)} className="BookingWindow">
          <BWContent additionalClass="space">
            <div className="loading-wrap">
              <div className="loading-anim">
                <div className="loading-title">
                  <span>{t('common.loading')}</span>
                </div>
              </div>
              <div className="loading-feedback">{isFetching ? t('scheduler.bookingSummary.bookingAppointment') : t('scheduler.bookingSummary.ata')}</div>
            </div>
          </BWContent>
        </div>
      );
    }

    const { addedServices, clientId, deposit, isRepeatBooking } = this.props;
    const { dialogOpened, confirmDialogOpen, openPosModal, appointmentId } = this.state;
    const firstAppointment = addedServices[0].appointment;

    const totalPrice = addedServices.filter((service) => !service.deleted).reduce((total, { price }) => total + price, 0);

    const totalTime = addedServices.filter(({ deleted }) => !deleted).reduce((total, { duration }) => total + moment.duration(duration).asMinutes(), 0);

    const variablePricing = addedServices.filter((service) => !service.deleted).findIndex((service) => service.variable_pricing === true) !== -1;

    return (
      <BWFooter additionalClass="apointment-footer border-t sticky-bottom d-flex">
        <SalonModal tag="BookingSummary__modal--exit" isOpen={confirmDialogOpen} withFooter>
          <SalonModal.Body>
            <div className={CN.modalContent}>
              <div className={CN.modalTitle}>{t('scheduler.bookingSummary.exitModal.title')}</div>
              <span className={CN.modalText}>
                {t('scheduler.bookingSummary.exitModal.description.part1')}
                <p>{t('scheduler.bookingSummary.exitModal.description.part2')}</p>
              </span>
            </div>
          </SalonModal.Body>
          <SalonModal.Footer>
            <div className={CN.modalButtons}>
              <Button variant="outlineForce" onClick={this.onRequestClose}>
                <span className={CN.buttonSpace}>{t('scheduler.bookingSummary.exitModal.keepEditingButton')}</span>
              </Button>
              <Button variant="primaryForce" onClick={this.onExitAnyway}>
                <span className={CN.buttonSpace}>{t('scheduler.bookingSummary.exitModal.exitButton')}</span>
              </Button>
            </div>
          </SalonModal.Footer>
        </SalonModal>

        <SalonModal tag="ProcedureModal__modal--delete-repeating-booking" isOpen={dialogOpened && isRepeatBooking} withFooter>
          <SalonModal.Body>
            <IconStatus variant="info" width="lg">
              <Icon variant="arrowPath" unselected width="l1" />
            </IconStatus>
            <div className={CN.modalContent}>
              <div className={CN.modalTitle}>{t('scheduler.bookingSummary.repeatingBookingModal.title')}</div>
              <div className={CN.modalText}>{t('scheduler.bookingSummary.repeatingBookingModal.description')}</div>
            </div>
          </SalonModal.Body>
          <SalonModal.Footer>
            <div className={CN.modalButtons}>
              <div />
              <Button
                variant="primaryForce"
                onClick={() => {
                  this.onRequestClose();
                  this.save('one');
                }}
              >
                <span className={CN.buttonSpace}>{t('scheduler.bookingSummary.repeatingBookingModal.confirmButton')}</span>
              </Button>
            </div>
          </SalonModal.Footer>
        </SalonModal>

        {openPosModal && <PosDepositDialog appointment={firstAppointment} appointmentId={appointmentId} deposit={deposit} clientId={clientId} />}

        <div data-bem="BookingSummary__content" className="flex-footer">
          <div
            className={cn({
              repeat: isRepeatBooking,
            })}
          >
            <div className="f-b" style={{ paddingBottom: 6 }}>
              <strong>
                {variablePricing && <span>{t('scheduler.bookingSummary.from')} </span>}
                <CurrencySign value={Math.round((totalPrice + 0.00001) * 100) / 100} />
              </strong>
              <span> {S.dot} </span>
              <strong>{moment.duration(totalTime, 'minutes').format('h [hr] m [mins]', { trim: 'both final' })}</strong>
            </div>
          </div>

          <button data-bem="BookingSummary__save-button" className="btn-booking" onClick={this.handleSave}>
            {t('scheduler.bookingSummary.saveBookingButton')}
          </button>
        </div>
      </BWFooter>
    );
  }
}

export { BookingSummary as BookingSummary4Test };

export default withTranslation()(BookingSummary);
